import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { api, errMsg, img, inr, STATUS, StatusBadge } from "@/lib/api";
import { ActionDialog, Btn, Field, PageHead, Panel } from "@/admin/ui";

const TABS = [["", "All"], ["awaiting_payment", "Awaiting payment"], ["under_verification", "Verifying"], ["paid", "Paid"], ["shipped", "Shipped"], ["delivered", "Delivered"], ["cancelled", "Cancelled"]];
const fmt = (d) => new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

const OrderSheet = ({ o, onClose, onChange }) => {
  const [status, setStatus] = useState(o.status);
  const [tr, setTr] = useState({ carrier: o.tracking?.carrier || "", number: o.tracking?.number || "", url: o.tracking?.url || "" });
  const [note, setNote] = useState("");
  const [refund, setRefund] = useState({ amount: o.total, reference: "" });
  const [dialog, setDialog] = useState(null);
  const put = async (body, msg) => {
    try { const { data } = await api.put(`/admin/orders/${o.order_number}`, body); onChange(data); toast.success(msg); } catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const c = o.customer;
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-[560px] overflow-y-auto bg-pearl" data-testid="order-sheet">
        <SheetTitle className="font-display text-[32px]">Order {o.order_number}</SheetTitle>
        <div className="flex gap-2 mt-2"><StatusBadge status={o.status} /><StatusBadge status={o.payment_status} /></div>
        <p className="text-[13px] text-plum/60 mt-2">{fmt(o.created_at)}</p>
        <div className="space-y-5 mt-6">
          <Panel title="Customer">
            <p className="font-bold">{c.name}</p>
            <p className="text-[15px]">{c.mobile} · {c.email}</p>
            <p className="text-[15px] text-plum/75 mt-1">{c.address}, {c.city}, {c.state} {c.pincode}</p>
          </Panel>
          <Panel title="Items">
            {o.items.map((l) => (
              <div key={l.product_id + l.variant} className="flex gap-3 py-2 items-center">
                <img src={img(l.image)} alt="" className="w-12 h-14 rounded-[10px] object-cover" />
                <p className="flex-1 text-[15px]"><b>{l.name}</b>{l.variant && ` · ${l.variant}`} × {l.quantity}</p>
                <b>{inr(l.line_total)}</b>
              </div>
            ))}
            <div className="border-t border-plum/10 mt-3 pt-3 text-[15px] space-y-1">
              <p className="flex justify-between"><span>Subtotal</span><b>{inr(o.subtotal)}</b></p>
              <p className="flex justify-between"><span>Shipping</span><b>{o.shipping ? inr(o.shipping) : "Free"}</b></p>
              <p className="flex justify-between text-[18px]"><span>Total</span><b data-testid="order-sheet-total">{inr(o.total)}</b></p>
              {o.utr && <p className="flex justify-between"><span>UTR</span><b>{o.utr}</b></p>}
              {o.refund && <p className="flex justify-between text-berry"><span>Refunded</span><b>{inr(o.refund.amount)} {o.refund.reference}</b></p>}
            </div>
          </Panel>
          {o.status !== "cancelled" && o.status !== "refunded" && (
            <Panel title="Update status">
              <div className="flex gap-2">
                <select className="rh-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="order-status-select">
                  {["awaiting_payment", "under_verification", "paid", "processing", "shipped", "delivered"].map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
                </select>
                <Btn onClick={() => put({ status }, "Status updated").catch(() => {})} data-testid="order-status-save">Save</Btn>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-5">
                <Field label="Courier"><input className="rh-input" value={tr.carrier} onChange={(e) => setTr({ ...tr, carrier: e.target.value })} data-testid="tracking-carrier" /></Field>
                <Field label="Tracking number"><input className="rh-input" value={tr.number} onChange={(e) => setTr({ ...tr, number: e.target.value })} data-testid="tracking-number" /></Field>
                <Field label="Tracking link" className="col-span-2"><input className="rh-input" value={tr.url} onChange={(e) => setTr({ ...tr, url: e.target.value })} /></Field>
              </div>
              <Btn variant="light" className="mt-3" onClick={() => put({ tracking_carrier: tr.carrier, tracking_number: tr.number, tracking_url: tr.url }, "Tracking saved").catch(() => {})} data-testid="tracking-save">Save tracking</Btn>
              <div className="flex flex-wrap gap-2 mt-6 pt-5 border-t border-plum/10">
                {!["shipped", "delivered"].includes(o.status) && <Btn variant="red" onClick={() => setDialog("cancel")} data-testid="order-cancel-button">Cancel order</Btn>}
                {o.payment_status === "paid" && <Btn variant="light" onClick={() => setDialog("refund")} data-testid="order-refund-button">Record refund</Btn>}
              </div>
            </Panel>
          )}
          <Panel title="Notes & history">
            <div className="flex gap-2">
              <input className="rh-input" placeholder="Add an internal note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="order-note-input" />
              <Btn onClick={() => note && put({ note }, "Note added").then(() => setNote("")).catch(() => {})} data-testid="order-note-save">Add</Btn>
            </div>
            <ul className="mt-4 space-y-2 text-[14px]">
              {[...(o.notes || []).map((n) => ({ ...n, note: true })), ...(o.history || [])].sort((a, b) => b.at.localeCompare(a.at)).map((h, i) => (
                <li key={i} className="flex gap-3"><span className="text-plum/50 w-32 flex-none">{fmt(h.at)}</span><span className={h.note ? "font-bold" : ""}>{h.note ? `Note: ${h.text}` : h.event}</span></li>
              ))}
            </ul>
          </Panel>
        </div>
        <ActionDialog open={dialog === "cancel"} onClose={() => setDialog(null)} title="Cancel this order?" description="Stock will be returned to inventory. If the customer has paid, record a refund separately." confirmLabel="Cancel order" variant="red" testId="cancel-dialog" onConfirm={() => put({ status: "cancelled" }, "Order cancelled")} />
        <ActionDialog open={dialog === "refund"} onClose={() => setDialog(null)} title="Record a refund" description={
          <span className="block space-y-3 mt-2">
            <input className="rh-input" type="number" value={refund.amount} onChange={(e) => setRefund({ ...refund, amount: e.target.value })} data-testid="refund-amount" />
            <input className="rh-input" placeholder="Refund reference / UTR" value={refund.reference} onChange={(e) => setRefund({ ...refund, reference: e.target.value })} data-testid="refund-reference" />
          </span>} confirmLabel="Record refund" testId="refund-dialog" onConfirm={() => put({ status: "refunded", refund_amount: Number(refund.amount), refund_reference: refund.reference }, "Refund recorded")} />
      </SheetContent>
    </Sheet>
  );
};

export default function Orders() {
  const [tab, setTab] = useState("");
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/orders", { params: tab ? { status: tab } : {} }).then((r) => setList(r.data));
  useEffect(() => { load(); }, [tab]); // eslint-disable-line
  return (
    <div data-testid="admin-orders">
      <PageHead title="Orders" sub="Tap an order to see details, update status or add tracking." />
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-5">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`h-10 px-4 rounded-[12px] text-[13px] font-bold whitespace-nowrap ${tab === k ? "bg-plum text-pearl" : "bg-white border border-plum/10"}`} data-testid={`orders-tab-${k || "all"}`}>{l}</button>)}
      </div>
      <div className="rounded-[22px] bg-white border border-plum/10 divide-y divide-plum/10">
        {list?.length === 0 && <p className="p-6 text-plum/60">No orders here yet.</p>}
        {(list || []).map((o) => (
          <button key={o.order_number} onClick={() => setOpen(o)} className="w-full text-left flex flex-wrap items-center gap-3 p-4 hover:bg-pearl/60" data-testid={`order-row-${o.order_number}`}>
            <span className="font-extrabold w-24">{o.order_number}</span>
            <span className="flex-1 min-w-[140px]"><b>{o.customer.name}</b><span className="block text-[13px] text-plum/60">{fmt(o.created_at)} · {o.items.length} item(s)</span></span>
            <StatusBadge status={o.status} />
            <span className="font-bold w-24 text-right">{inr(o.total)}</span>
          </button>
        ))}
      </div>
      {open && <OrderSheet o={open} onClose={() => setOpen(null)} onChange={(d) => { setOpen(d); load(); }} />}
    </div>
  );
}
